/**
 * MM/DD/YYYY mask + validation helpers for the html/js build pipeline.
 *
 * Plain-JS port of the React app's canonical helpers — keep in sync when
 * either side changes:
 *   - src/wp-content/themes/eaplus/src/ghp/lib/dateMask.ts (masking + caret math)
 *   - src/wp-content/themes/eaplus/src/ghp/lib/date.ts      (parseDob + isFutureDate)
 * The TS versions ship in the separately-built family-enrollment bundle and
 * can't be imported here, so the logic is duplicated verbatim.
 */

export const DIGIT_LIMIT = 8;
export const FULL_LENGTH = 10; // "MM/DD/YYYY"

const MIN_YEAR = 1900;
const DOB_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

const SEPARATED_US_DATE = /^\s*(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})\s*$/;
const ISO_DATE = /^\s*(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})\s*$/;

export function isBulkInsert(event) {
  const { inputType, data } = event;
  return (
    !inputType ||
    inputType === 'insertReplacementText' ||
    inputType.startsWith('insertFrom') ||
    (inputType === 'insertText' && !!data && data.length > 1)
  );
}

export function expandAutofilledDate(rawValue) {
  const iso = rawValue.match(ISO_DATE);
  if (iso) {
    return `${iso[2].padStart(2, '0')}${iso[3].padStart(2, '0')}${iso[1]}`;
  }
  const us = rawValue.match(SEPARATED_US_DATE);
  if (us) {
    return `${us[1].padStart(2, '0')}${us[2].padStart(2, '0')}${us[3]}`;
  }
  return rawValue;
}

function isDigitCharCode(code) {
  return code >= 48 && code <= 57;
}

function clampSegment(segment, min, max) {
  if (segment.length === 0) return '';
  const n = Number(segment);
  if (n > max) return String(max).padStart(segment.length, '0');
  if (segment.length === 2 && n < min) return String(min).padStart(2, '0');
  return segment;
}

function clampYear(segment) {
  if (segment.length === 0) return '';
  if (segment.length < 4) return segment;
  const n = Number(segment);
  if (n < MIN_YEAR) return String(MIN_YEAR);
  return segment;
}

/**
 * Format an arbitrary user-typed value into a partial MM/DD/YYYY string.
 * Month clamps to 01-12, day to 01-31, year floored at 1900 once 4 digits
 * are present. Future years pass through (validated separately).
 */
export function formatDateMask(rawValue) {
  const digits = rawValue.replace(/\D/g, '').slice(0, DIGIT_LIMIT);

  if (digits.length === 0) {
    return '';
  }

  const mm = clampSegment(digits.slice(0, Math.min(digits.length, 2)), 1, 12);
  if (digits.length <= 1) {
    return mm;
  }
  if (digits.length === 2) {
    return `${mm}/`;
  }

  const dd = clampSegment(digits.slice(2, Math.min(digits.length, 4)), 1, 31);
  if (digits.length === 3) {
    return `${mm}/${dd}`;
  }
  if (digits.length === 4) {
    return `${mm}/${dd}/`;
  }

  const yyyy = clampYear(digits.slice(4));
  return `${mm}/${dd}/${yyyy}`;
}

/** Count digit characters in value[0..position). */
export function countDigitsBefore(value, position) {
  const limit = Math.min(Math.max(position, 0), value.length);
  let count = 0;
  for (let i = 0; i < limit; i += 1) {
    if (isDigitCharCode(value.charCodeAt(i))) {
      count += 1;
    }
  }
  return count;
}

/** Caret position immediately after the Nth digit, skipping trailing slashes. */
export function caretPositionAfterDigits(formatted, digitCount) {
  if (digitCount <= 0) {
    return 0;
  }

  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (isDigitCharCode(formatted.charCodeAt(i))) {
      seen += 1;
      if (seen === digitCount) {
        let next = i + 1;
        while (next < formatted.length && formatted[next] === '/') {
          next += 1;
        }
        return next;
      }
    }
  }
  return formatted.length;
}

/**
 * Parse a strict MM/DD/YYYY string into a Date (local time). Returns null for
 * malformed strings and calendar rollovers such as 02/31/2020.
 */
export function parseDob(dob) {
  const match = dob.match(DOB_PATTERN);
  if (!match) {
    return null;
  }

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);

  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

/** True when dob is a valid calendar date that falls after `now`. */
export function isFutureDate(dob, now = new Date()) {
  const date = parseDob(dob);
  return date !== null && date.getTime() > now.getTime();
}

/** True when dob is a fully valid MM/DD/YYYY calendar date. */
export function isValidDob(dob) {
  return parseDob(dob) !== null;
}
